/**
 * Data + formatting layer behind the core KPI grid (PT-5711).
 *
 * Every figure the grid shows is a pure function of the OfferNode already in memory — the offer's
 * own precomputed fields (aum, totalDepositSum, lifetimeWithdrawals, totalInvestments, pfCollected30d,
 * nodePnl30d, settings) and a walk of its already-loaded descendants for the downline roll-up.
 * Nothing here issues a network call, so the tiles are stable per node ID across renders (AC).
 *
 * All three 30-day metrics are live server-computed figures: PF Collected (PT-5784), Node PnL
 * (PT-5809) and MM Trades (PT-5815). {@link PLACEHOLDER} now only ever stands for a figure the
 * server could not supply, never for one that was never sourced.
 */
import {PammOfferSettingsTradingIntervalType} from '../../../graphql/types'
import {OfferNode} from '../types'
import {getOfferChainIds} from './buildOfferTree'

/** En-dash shown for a metric that is missing or not yet sourced (matches the investor book's "–"). */
export const PLACEHOLDER = '–'

/**
 * Money as an integer amount with thousands separators, no decimals (AC: "USD, integer formatting
 * with thousands separators"). A missing or non-finite amount renders as {@link PLACEHOLDER}.
 *
 * @param amount - The amount in whole currency units.
 * @param currency - ISO currency code, e.g. `USD`.
 * @param locale - Optional locale; defaults to the runtime (user) locale.
 */
export const formatKpiMoney = (
  amount: number | null | undefined,
  currency: string,
  locale?: string,
): string =>
  amount != null && Number.isFinite(amount)
    ? new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount)
    : PLACEHOLDER

/** Whole count with thousands separators; {@link PLACEHOLDER} when missing (e.g. an investor count of 0 still shows "0"). */
export const formatKpiCount = (count: number | null | undefined, locale?: string): string =>
  count != null && Number.isFinite(count) ? new Intl.NumberFormat(locale).format(count) : PLACEHOLDER

/**
 * Performance fee as an integer percentage (AC: "integers for fee percentages"). The offer's fee is
 * tiered; the grid shows the entry (first) tier's rate, matching the card's existing fee stat.
 *
 * @param value - The fee percentage, e.g. `20` for 20%.
 */
export const formatFeePercent = (value: number | null | undefined): string =>
  value != null && Number.isFinite(value) ? `${Math.round(value)}%` : PLACEHOLDER

/**
 * Signed percentage at one decimal place (AC: "1 decimal for PnL values"), e.g. `+3.2%` / `-1.0%`.
 * Zero renders as `0.0%` with no sign.
 *
 * Not used by the Node PnL tile: what the server sources for it is an amount, and there is no
 * per-node cost basis to turn that into a return, so that tile renders money via
 * {@link formatKpiMoney} and takes only its colour from {@link pnlTone}.
 *
 * @param percent - The PnL as a percentage.
 */
export const formatPnl = (percent: number | null | undefined): string => {
  if (percent == null || !Number.isFinite(percent)) return PLACEHOLDER
  const sign = percent > 0 ? '+' : ''
  return `${sign}${percent.toFixed(1)}%`
}

/**
 * Net lifetime flow: deposits (LTD) minus withdrawals (LTD) — the money that has stayed in the offer
 * (PT-5715 AC: "Net flow displayed equals Deposits (LTD) minus Withdrawals (LTD)"). Returns `null`
 * when either side is missing, so the tile renders a placeholder rather than a half-computed figure
 * that would not reconcile against the two tiles it is derived from.
 *
 * @param deposits - Lifetime deposits (the Deposits tile's value).
 * @param withdrawals - Lifetime withdrawals (the Withdrawals tile's value).
 * @returns The signed net flow, or `null` when it cannot be computed.
 */
export const computeNetFlow = (
  deposits: number | null | undefined,
  withdrawals: number | null | undefined,
): number | null => {
  if (deposits == null || !Number.isFinite(deposits)) return null
  if (withdrawals == null || !Number.isFinite(withdrawals)) return null
  return deposits - withdrawals
}

/** Sign of a PnL figure, driving the tile colour (AC: positive green, negative red, zero neutral grey). */
export type PnlTone = 'positive' | 'negative' | 'neutral'

/**
 * Classifies a PnL value for colouring. A missing value is neutral, not an error — the tile simply
 * has no P/L to colour (used for the not-yet-sourced 30d tiles, which render neutral).
 */
export const pnlTone = (value: number | null | undefined): PnlTone => {
  if (value == null || !Number.isFinite(value) || value === 0) return 'neutral'
  return value > 0 ? 'positive' : 'negative'
}

/**
 * Sums the current AUM of every node below `node` (its downline), excluding the node itself — the
 * "Downline Funds" figure. Walks the already-loaded children, so it costs nothing and cannot go
 * stale relative to the rendered tree (PT-5711/PT-5714).
 *
 * @param node - The node whose descendants to roll up.
 * @returns The summed downline AUM in whole currency units.
 */
export const computeDownlineFunds = (node: OfferNode): number => {
  let sum = 0
  const visit = (current: OfferNode): void => {
    current.children.forEach((child) => {
      sum += child.offer.aum ?? 0
      visit(child)
    })
  }
  visit(node)
  return sum
}

/** Number of descendant nodes below `node` (its downline size), for the Downline Funds sub-line. */
export const countDescendants = (node: OfferNode): number => {
  let count = 0
  const visit = (current: OfferNode): void => {
    current.children.forEach((child) => {
      count += 1
      visit(child)
    })
  }
  visit(node)
  return count
}

/**
 * The role a node plays in the tree, which decides *which* KPI tiles make sense for it (PT-5712).
 * The tree carries no explicit role field, so role is derived positionally from the same signals
 * the card header already uses: an empty agent chain marks the Money Manager (master), and among
 * agent offers, one that distributes further (has sub-offers or its own investors) is an operating
 * agent, while a leaf with no downline is a pure investor position.
 */
export type NodeRole = 'master' | 'agent' | 'investor'

/**
 * Classifies a node into a {@link NodeRole}.
 *
 * - `master` — the Money Manager's own offer (empty agent chain).
 * - `agent` — an agent offer that distributes further: it has sub-offers or at least one investor.
 *   The rendered root of an Agent view is always the agent's own top offer, so a parentless
 *   non-MM node is an agent too, even with an empty book.
 * - `investor` — a non-root agent offer with no downline (no sub-offers and no investors), i.e. a
 *   leaf that only invests upstream. It gets the investor-facing tiles (Your Share, Perf. Fee).
 *
 * @param node - The tree node to classify.
 */
export const getNodeRole = (node: OfferNode): NodeRole => {
  if (getOfferChainIds(node.offer).length === 0) return 'master'
  // A root (parentless) node in these views is the agent's own top offer, never a pure investor.
  if (node.parent == null) return 'agent'
  const hasDownline = node.children.length > 0 || (node.offer.totalInvestments ?? 0) > 0
  return hasDownline ? 'agent' : 'investor'
}

/**
 * The node's share of its parent's AUM — an investor node's "Your Share (of parent AUM)" figure
 * (PT-5712). Returns a ratio in `[0, 1]`, or `null` when it cannot be computed: a root has no
 * parent, and a parent with zero/unknown AUM has no denominator. Both operands are already in
 * memory (each node's `aum`), so this stays a pure, network-free calculation.
 *
 * @param node - The node whose parent share to compute.
 * @returns The ratio of the node's AUM to its parent's, or `null` when undefined.
 */
export const computeParentAumShare = (node: OfferNode): number | null => {
  const parentAum = node.parent?.offer.aum
  const ownAum = node.offer.aum
  if (parentAum == null || !Number.isFinite(parentAum) || parentAum <= 0) return null
  if (ownAum == null || !Number.isFinite(ownAum)) return null
  return ownAum / parentAum
}

/** A ratio in `[0, 1]` as a one-decimal percentage (e.g. `12.5%`); {@link PLACEHOLDER} when undefined. */
export const formatShare = (ratio: number | null | undefined): string =>
  ratio != null && Number.isFinite(ratio) ? `${(ratio * 100).toFixed(1)}%` : PLACEHOLDER

/**
 * Win rate as a whole percentage, e.g. `58%` (PT-5815). Deliberately coarser than
 * {@link formatShare}: it is a ratio over a handful of trades, and a decimal place would imply a
 * precision the sample does not have. {@link PLACEHOLDER} when the manager closed nothing in the
 * window — a win rate over no trades is undefined, not 0%.
 *
 * @param ratio - The winning share as a fraction in [0, 1].
 */
export const formatWinRate = (ratio: number | null | undefined): string =>
  ratio != null && Number.isFinite(ratio) ? `${Math.round(ratio * 100)}%` : PLACEHOLDER

/**
 * Human label for an offer's payout/trading interval, e.g. `1 Days` (PT-5712 Offer Details).
 * Renders {@link PLACEHOLDER} when either half is missing.
 *
 * @param interval - The offer's `settings.tradingInterval`.
 */
export const formatTradingInterval = (
  interval: PammOfferSettingsTradingIntervalType | null | undefined,
): string =>
  interval?.count != null && interval.type ? `${interval.count} ${interval.type}` : PLACEHOLDER
